/* eslint-disable */
import React, { useState, type FC, Fragment } from 'react';
import {
  type IEmployerProps,
  type IAdminDetails,
  IAdminDetailsArray,
} from 'types/pages';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  // { type SelectChangeEvent }
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import {
  emailValidator,
  IBANBankNumberValidation,
  requiredValidator,
} from 'utils/validationUtils';
import { createOrganization } from 'service/EmployerService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { useDispatch } from 'react-redux';
import MuiPhoneNumber from 'material-ui-phone-number';
import { Checkbox, FormControlLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  addEmployerConstantTexts,
  pepTypeOptions,
  genderOptions,
  maritalStatusOptions,
  payrollFrequencyOptions,
} from 'constants/constantText';
import { useSelector } from 'react-redux';
import type { RootState } from 'store/rootReducer';
import { FormHelperText } from '@mui/material';
import { BankAccountNumberLength } from 'utils/Constants';

const useStyles = makeStyles({
  labelStyle: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: '1.4375em',
  },
});
const initialEmployerState: IEmployerProps = {
  companyName: '',
  payrollFrequency: '',
  regStreetOne: '',
  regStreetTwo: '',
  regStreetThree: '',
  regCity: '',
  regState: '',
  regCountry: '',
  regPostalCode: '',
  regPhoneNumber: '',
  regEmail: '',
  employerTaxDetails: '',
  employerBankAccountName: '',
  employerBankAccountNumber: '',
  tradingStreetOne: '',
  tradingStreetTwo: '',
  tradingStreetThree: '',
  tradingCity: '',
  tradingState: '',
  tradingCountry: '',
  tradingPostalCode: '',
  tradingPhoneNumber: '',
  tradingEmail: '',
  isActive: true,
  adminOption: 'later',
  adminDetails: [],
  sameAsAbove: false,
  payrollDate: '',
  createdByAcorn: false,
  comments: '',
};

const textFieldInputType = (field: string): string =>
  [
    'employeeContribution',
    'employerContribution',
    'employeeContributionPercentage',
    'employerContributionPercentage',
  ].includes(field)
    ? 'number'
    : 'text';

const notRequiredFields = (field: string): boolean =>
  !['employeeId', 'department', 'designation'].includes(field);

const AddEmployer: FC<any> = ({
  setIsModalOpen,
  open,
  onClose,
  getupdateEmployerData,
}): JSX.Element => {
  const classes = useStyles();
  const [formData, setFormData] =
    useState<IEmployerProps>(initialEmployerState);
  const [errors, setErrors] = useState<Partial<IEmployerProps>>({});
  const dispatch = useDispatch();
  const { economicStatusAndOccupation } = useSelector(
    (state: RootState) => state.employeeDetails,
  );
  const [formError, setFormError] = useState<IEmployerProps>({
    companyName: '',
    payrollFrequency: '',
    regCity: '',
    regState: '',
    regCountry: '',
    regPostalCode: '',
    regPhoneNumber: '',
    regEmail: '',
    employerTaxDetails: '',
    employerBankAccountName: '',
    employerBankAccountNumber: '',
    payrollDate: '',
    comments: '',
    tradingCity: '',
    tradingState: '',
    tradingCountry: '',
    tradingPostalCode: '',
    tradingPhoneNumber: '',
    tradingEmail: '',
    isActive: true,
    adminDetails: [],
    sameAsAbove: '',
  });
  const [loader, setLoader] = useState(false);
  const handlePhoneChange = (value: string, fieldName: string): void => {
    if (fieldName === 'regPhoneNumber') {
      setFormData({ ...formData, [fieldName]: value });
      setErrors({ ...errors, [fieldName]: '' });
    }
    if (fieldName === 'tradingPhoneNumber') {
      setFormData({ ...formData, [fieldName]: value });
      setErrors({ ...errors, [fieldName]: '' });
    }
    if (fieldName === 'phone') {
      const updatedAdminDetails = formData.adminDetails?.map(
        (admin: IAdminDetails) => {
          return {
            ...admin,
            phone: value,
          };
        },
      );

      setFormData({
        ...formData,
        adminDetails: updatedAdminDetails,
      });
    }
  };

  const employerInputField = [
    {
      label: strings.displayText.companyNameLabel,
      name: addEmployerConstantTexts.companyName,
      value: formData.companyName,
      helperText: formError?.companyName || errors?.companyName,
      type: 'text',
    },
    {
      label: strings.displayText.payrollFrequencyLabel,
      name: addEmployerConstantTexts.payrollFrequency,
      value: formData.payrollFrequency,
      helperText: formError?.payrollFrequency || errors?.payrollFrequency,
      type: 'text',
    },
    {
      label: strings.displayText.registerEmailLabel,
      name: addEmployerConstantTexts.regEmail,
      value: formData.regEmail,
      helperText: formError?.regEmail || errors?.regEmail,
      type: 'email',
    },
    {
      label: strings.displayText.registerPhoneNoLabel,
      name: addEmployerConstantTexts.regPhoneNumber,
      value: formData.regPhoneNumber,
      helperText: formError?.regPhoneNumber || errors?.regPhoneNumber,
      type: 'number',
    },
    {
      label: strings.displayText.registerStreetOneLabel,
      name: addEmployerConstantTexts.regStreetOne,
      value: formData.regStreetOne,
      type: 'text',
    },
    {
      label: strings.displayText.registerStreetTwoLabel,
      name: addEmployerConstantTexts.regStreetTwo,
      value: formData.regStreetTwo,
      type: 'text',
    },
    {
      label: strings.displayText.registerStreetThreeLabel,
      name: addEmployerConstantTexts.regStreetThree,
      value: formData.regStreetThree,
      type: 'text',
    },
    {
      label: strings.displayText.registerCityLabel,
      name: addEmployerConstantTexts.regCity,
      value: formData.regCity,
      type: 'text',
    },
    {
      label: strings.displayText.registerStateLabel,
      name: addEmployerConstantTexts.regState,
      value: formData.regState,
      helperText: formError?.regState || errors?.regState,
      type: 'text',
    },
    {
      label: strings.displayText.registerCountryLabel,
      name: addEmployerConstantTexts.regCountry,
      value: formData.regCountry,
      helperText: formError?.regCountry || errors?.regCountry,
      type: 'text',
    },
    {
      label: strings.displayText.registerEircodeLabel,
      name: addEmployerConstantTexts.regPostalCode,
      value: formData.regPostalCode,
      helperText: formError?.regPostalCode || errors?.regPostalCode,
      type: 'text',
    },
    {
      label: strings.displayText.employerTaxDetails,
      name: addEmployerConstantTexts.employerTaxDetails,
      value: formData.employerTaxDetails,
      helperText: formError?.employerTaxDetails || errors?.employerTaxDetails,
      type: 'text',
    },
    {
      label: strings.displayText.employerBankAccountName,
      name: addEmployerConstantTexts.employerBankAccountName,
      value: formData.employerBankAccountName,
      helperText:
        formError?.employerBankAccountName || errors?.employerBankAccountName,
      type: 'text',
    },
    {
      label: strings.displayText.employerBankAccountNumber,
      name: addEmployerConstantTexts.employerBankAccountNumber,
      value: formData.employerBankAccountNumber,
      helperText:
        formError?.employerBankAccountNumber ||
        errors?.employerBankAccountNumber,
      type: 'text',
    },
    {
      label: strings.displayText.payrollDate,
      name: addEmployerConstantTexts.payrollDate,
      value: formData.payrollDate,
      helperText: formError?.payrollDate || errors?.payrollDate,
      type: 'text',
    },
    {
      label: strings.displayText.comments,
      name: addEmployerConstantTexts.comments,
      value: formData.comments,
      type: 'text',
    },
    {
      label: strings.displayText.sameAsAboveLabel,
      name: addEmployerConstantTexts.sameAsAbove,
      value: formData.sameAsAbove,
      helperText: formError?.sameAsAbove || errors?.sameAsAbove,
    },
    {
      label: strings.displayText.tradingEmailLabel,
      name: addEmployerConstantTexts.tradingEmail,
      value: formData.tradingEmail,
      helperText: formError?.tradingEmail || errors?.tradingEmail,
      type: 'email',
      isRequired: false,
    },
    {
      label: strings.displayText.tradingPhoneNoLabel,
      name: addEmployerConstantTexts.tradingPhoneNumber,
      value: formData.tradingPhoneNumber,
      helperText: formError?.tradingPhoneNumber || errors?.tradingPhoneNumber,
      type: 'number',
      isRequired: false,
    },

    {
      label: strings.displayText.tradingStreetOneLabel,
      name: addEmployerConstantTexts.tradingStreetOne,
      value: formData.tradingStreetOne,
      type: 'text',
    },
    {
      label: strings.displayText.tradingStreetTwoLabel,
      name: addEmployerConstantTexts.tradingStreetTwo,
      value: formData.tradingStreetTwo,
      type: 'text',
    },
    {
      label: strings.displayText.tradingStreetThreeLabel,
      name: addEmployerConstantTexts.tradingStreetThree,
      value: formData.tradingStreetThree,
      type: 'text',
    },
    {
      label: strings.displayText.tradingcityLabel,
      name: addEmployerConstantTexts.tradingCity,
      value: formData.tradingCity,
      type: 'text',
    },
    {
      label: strings.displayText.tradingStateLabel,
      name: addEmployerConstantTexts.tradingState,
      value: formData.tradingState,
      helperText: formError?.tradingState || errors?.tradingState,
      type: 'text',
    },
    {
      label: strings.displayText.tradingCountryLabel,
      name: addEmployerConstantTexts.tradingCountry,
      value: formData.tradingCountry,
      helperText: formError?.tradingCountry || errors?.tradingCountry,
      type: 'text',
    },
    {
      label: strings.displayText.tradingEircodeLabel,
      name: addEmployerConstantTexts.tradingPostalCode,
      value: formData.tradingPostalCode,
      helperText: formError?.tradingPostalCode || errors?.tradingPostalCode,
      type: 'text',
    },
  ];
  const fieldNames: { [key: string]: string } = {
    firstName: strings.displayText.firstName,
    lastName: strings.displayText.lastName,
    email: strings.displayText.emailAddress,
    phone: strings.displayText.phoneNumber,
    gender: strings.displayText.gender,
    maritalStatus: strings.displayText.maritalStatus,
    economicStatus: strings.displayText.economicStatus,
    economicOccupation: strings.displayText.economicOccupation,
    pepType: strings.displayText.pepTypeText,
    employeeId: strings.displayText.employeeID,
    designation: strings.displayText.desigination,
    department: strings.displayText.department,
    employeeContribution: strings.displayText.employeeContribution,
    employerContribution: strings.displayText.employerContribution,
    employeeContributionPercentage:
      strings.displayText.employeeContributionPercent,
    employerContributionPercentage:
      strings.displayText.employerContributionPercent,
  };
  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === addEmployerConstantTexts.employerBankAccountNumber) {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    handleValidation(event);
  };

  const handleSwitchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData({ ...formData, [event.target.name]: event.target.checked });
  };

  const handleAdminInputValidation = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    const updatedAdmins = [
      ...(formError.adminDetails || []),
    ] as IAdminDetailsArray;

    // Initialize object at specified index if it doesn't exist
    if (!updatedAdmins[index]) {
      updatedAdmins[index] = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: '',
        maritalStatus: '',
        economicOccupation: 0,
        economicStatus: 0,
        pepType: undefined,
      };
    }
    switch (name) {
      case addEmployerConstantTexts.firstName: {
        if (requiredValidator(value)) {
          updatedAdmins[index].firstName = requiredValidator(value) || '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        } else {
          updatedAdmins[index].firstName = '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        }
        break;
      }
      case addEmployerConstantTexts.lastName: {
        if (requiredValidator(value)) {
          updatedAdmins[index].lastName = requiredValidator(value) || '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        } else {
          updatedAdmins[index].lastName = '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        }
        break;
      }
      case addEmployerConstantTexts.email: {
        if (emailValidator(value)) {
          updatedAdmins[index].email = emailValidator(value) || '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        } else {
          updatedAdmins[index].email = '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        }
        break;
      }
      case addEmployerConstantTexts.phone: {
        // Normalize to avoid double + (e.g. ++353 -> +353)
        const normalizedPhone =
          typeof value === 'string' ? value.replace(/^\+\+/, '+') : value;
        if (requiredValidator(normalizedPhone)) {
          updatedAdmins[index].phone = requiredValidator(normalizedPhone) || '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        } else {
          updatedAdmins[index].phone = '';
          setFormError({ ...formError, adminDetails: updatedAdmins });
        }
        break;
      }
      default:
        break;
    }
  };

  const handleValidation = (e: {
    target: {
      name: string;
      value: string;
    };
  }): void => {
    let updatedErrors = { ...errors };

    switch (e.target.name) {
      case addEmployerConstantTexts.companyName: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            companyName: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, companyName: '' });
        }
        delete updatedErrors.companyName;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.payrollFrequency: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            payrollFrequency: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, payrollFrequency: '' });
        }
        delete updatedErrors.payrollFrequency;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.regEmail: {
        if (emailValidator(e.target.value)) {
          setFormError({
            ...formError,
            regEmail: emailValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, regEmail: '' });
        }
        delete updatedErrors.regEmail;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.regPhoneNumber: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            regPhoneNumber: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, regPhoneNumber: '' });
        }
        delete updatedErrors.regPhoneNumber;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.regCountry: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            regCountry: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, regCountry: '' });
        }
        delete updatedErrors.regCountry;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.regPostalCode: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            regPostalCode: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, regPostalCode: '' });
        }
        delete updatedErrors.regPostalCode;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.tradingCountry: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            tradingCountry: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, tradingCountry: '' });
        }
        delete updatedErrors.tradingCountry;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.tradingPostalCode: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            tradingPostalCode: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, tradingPostalCode: '' });
        }
        delete updatedErrors.tradingPostalCode;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.tradingPhoneNumber: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            tradingPhoneNumber: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, tradingPhoneNumber: '' });
        }
        delete updatedErrors.tradingPhoneNumber;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.tradingEmail: {
        if (emailValidator(e.target.value)) {
          setFormError({
            ...formError,
            tradingEmail: emailValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, tradingEmail: '' });
        }
        delete updatedErrors.tradingEmail;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.employerTaxDetails: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            employerTaxDetails: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, employerTaxDetails: '' });
        }
        delete updatedErrors.employerTaxDetails;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.employerBankAccountName: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            employerBankAccountName: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, employerBankAccountName: '' });
        }
        delete updatedErrors.employerBankAccountName;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.employerBankAccountNumber: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            employerBankAccountNumber: requiredValidator(e.target.value) || '',
          });
        } else if (IBANBankNumberValidation(e.target.value)) {
          setFormError({
            ...formError,
            employerBankAccountNumber: IBANBankNumberValidation(e.target.value),
          });
        } else {
          setFormError({ ...formError, employerBankAccountNumber: '' });
        }
        delete updatedErrors.employerBankAccountNumber;
        setErrors(updatedErrors);
        break;
      }
      case addEmployerConstantTexts.payrollDate: {
        if (requiredValidator(e.target.value)) {
          setFormError({
            ...formError,
            payrollDate: requiredValidator(e.target.value) || '',
          });
        } else {
          setFormError({ ...formError, payrollDate: '' });
        }
        delete updatedErrors.payrollDate;
        setErrors(updatedErrors);
        break;
      }
      default:
        break;
    }
  };

  const handleAdminDetailsChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (event.target.value === 'Add now') {
      setFormData({
        ...formData,
        adminOption: event.target.value,
        adminDetails: [
          {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            gender: '',
            maritalStatus: '',
            employeeId: '',
            designation: '',
            department: '',
            employeeContribution: '',
            employerContribution: '',
            employeeContributionPercentage: '',
            employerContributionPercentage: '',
            pepType: false,
            economicStatus: 0,
            economicOccupation: 0,
            pepType: false,
          },
        ],
      });
    } else {
      setFormData({
        ...formData,
        adminOption: event.target.value,
        adminDetails: [],
      });
    }
  };

  const handleAdminInputChange = (
    index: number,
    event?: React.ChangeEvent<HTMLInputElement>,
    objValue?: string,
    objKey?: string,
  ) => {
    let updateAdmins = [...(formData.adminDetails || [])] as IAdminDetailsArray;

    if (event?.target) {
      const { name, value } = event.target;

      if (name === 'firstName' || name === 'lastName') {
        const regex = /^[A-Za-z'\- ]*$/;
        if (regex.test(value)) {
          updateAdmins[index][name as keyof IAdminDetails] = value;
        } else {
          return;
        }
      } else {
        updateAdmins[index][name as keyof IAdminDetails] = value;
      }
    }

    if (objKey && objValue !== undefined) {
      updateAdmins[index][objKey as keyof IAdminDetails] = objValue;
    }

    setFormData({
      ...formData,
      adminDetails: updateAdmins,
    });
  };

  const getOptionItems = (field: string, index: number) => {
    switch (field) {
      case 'gender':
        return genderOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ));
      case 'maritalStatus':
        return maritalStatusOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ));
      case 'pepType':
        return pepTypeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ));
      case 'economicStatus':
        return economicStatusAndOccupation?.categories.map(
          (category: { id: number; categoryName: string }) => (
            <MenuItem key={category.id} value={category.id}>
              {category.categoryName}
            </MenuItem>
          ),
        );
      case 'economicOccupation':
        const economicStatus = formData.adminDetails?.[index].economicStatus;
        const selectedCategory = economicStatusAndOccupation?.categories.find(
          (category: { id: number }) => category.id === economicStatus,
        );
        if (selectedCategory) {
          return selectedCategory.subCategories.map(
            (subcategory: { id: number; categoryName: string }) => (
              <MenuItem key={subcategory.id} value={subcategory.id}>
                {subcategory.categoryName}
              </MenuItem>
            ),
          );
        } else {
          return null;
        }
      default:
        return null;
    }
  };

  const handleAddAdmin = () => {
    const updatedAdmins = formData.adminDetails || [];
    setFormData({
      ...formData,
      adminDetails: [
        ...updatedAdmins,
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          gender: '',
          maritalStatus: '',
          employeeId: '',
          designation: '',
          department: '',
          employeeContribution: '',
          employerContribution: '',
          employeeContributionPercentage: '',
          employerContributionPercentage: '',
          pepType: false,
          economicStatus: 0,
          economicOccupation: 0,
          pepType: false,
        },
      ],
    });
  };
  // useEffect(() => {
  //   if (Object.values(errors).every((value) => value === '')) {
  //     return;
  //   }
  //   const timer = setTimeout(() => {
  //     setErrors({});
  //   }, 3000);
  //   return () => clearTimeout(timer);
  // }, [errors]);

  const handleRemoveAdmin = (index: number) => {
    const newFormData = formData;
    const updatedAdmins = [...formData.adminDetails];
    updatedAdmins.splice(index, 1);
    if (updatedAdmins.length === 0) {
      newFormData.adminOption = 'later';
    }
    setFormData({ ...newFormData, adminDetails: updatedAdmins });
  };

  const validatePhoneNumber = (phone: string) => {
    // Remove the country code and non-numeric characters
    const number = phone.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
    return number.length === 9;
  };

  const handleFormValidate = (data: IEmployerProps) => {
    let errors: any = {};

    if (!data.companyName) {
      errors.companyName = strings.displayText.fillCompanyName;
    }

    if (!data.payrollFrequency) {
      errors.payrollFrequency = strings.displayText.fillPayrollFrequency;
    }

    if (!data.regEmail || emailValidator(data.regEmail)) {
      errors.regEmail = strings.displayText.fillRegEmail;
    }
    if (!data.regPhoneNumber) {
      errors.regPhoneNumber = !data.regPhoneNumber
        ? strings.displayText.fillRegPhone
        : strings.displayText.invalidRegPhone;
    }

    if (!data.regCountry) {
      errors.regCountry = strings.displayText.fillCountry;
    }

    if (!data.regPostalCode) {
      errors.regPostalCode = strings.displayText.fillPostalCode;
    }

    if (!data.tradingEmail || emailValidator(data.tradingEmail)) {
      errors.tradingEmail = strings.displayText.fillTradingEmail;
    }

    if (!data.tradingCountry) {
      errors.tradingCountry = strings.displayText.fillCountry;
    }

    if (!data.tradingPostalCode) {
      errors.tradingPostalCode = strings.displayText.fillPostalCode;
    }
    if (!data.employerTaxDetails) {
      errors.employerTaxDetails = strings.displayText.fillEmployerTaxDetails;
    }
    if (!data.employerBankAccountName) {
      errors.employerBankAccountName =
        strings.displayText.fillEmployerBankAccountName;
    }

    // check if bank account number is empty
    if (!data.employerBankAccountNumber) {
      errors.employerBankAccountNumber =
        strings.displayText.fillEmployerBankAccountNumber;
    }
    // check for account number validation
    if (
      data.employerBankAccountNumber &&
      IBANBankNumberValidation(data.employerBankAccountNumber)
    ) {
      errors.employerBankAccountNumber =
        strings.displayText.employerBankAccountNumberFormatCheck;
    }

    if (!data.payrollDate) {
      errors.payrollDate = strings.displayText.fillPayrollDate;
    }

    if (!data.tradingPhoneNumber) {
      errors.tradingPhoneNumber = !data.tradingPhoneNumber
        ? strings.displayText.fillTradingPhone
        : strings.displayText.invalidTradingPhone;
    }

    if (data.adminDetails.length > 0) {
      data.adminDetails.forEach((admin, index) => {
        if (!admin.firstName) {
          errors[`adminDetails[${index}].firstName`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.lastName) {
          errors[`adminDetails[${index}].lastName`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.email || emailValidator(admin.email)) {
          errors[`adminDetails[${index}].email`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.gender) {
          errors[`adminDetails[${index}].gender`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.maritalStatus) {
          errors[`adminDetails[${index}].maritalStatus`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.economicStatus) {
          errors[`adminDetails[${index}].economicStatus`] =
            strings.displayText.fillMandatoryFields;
        }
        // if id is 12 its not economically active
        if (admin.economicStatus !== 12 && admin.economicOccupation === 0) {
          errors[`adminDetails[${index}].economicOccupation`] =
            strings.displayText.fillMandatoryFields;
        }
        if (admin.pepType === undefined || admin.pepType === null) {
          errors[`adminDetails[${index}].pepType`] =
            strings.displayText.fillMandatoryFields;
        }

        if (!admin.employeeContribution) {
          errors[`adminDetails[${index}].employeeContribution`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.employerContribution) {
          errors[`adminDetails[${index}].employerContribution`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.employeeContributionPercentage) {
          errors[`adminDetails[${index}].employeeContributionPercentage`] =
            strings.displayText.fillMandatoryFields;
        }
        if (!admin.employerContributionPercentage) {
          errors[`adminDetails[${index}].employerContributionPercentage`] =
            strings.displayText.fillMandatoryFields;
        }

        const emailSet = new Set();
        const phoneSet = new Set();

        // Check for duplicate emails
        if (admin.email) {
          if (emailSet.has(admin.email)) {
            errors[`adminDetails[${index}].email`] =
              strings.displayText.duplicateEmailError;
          } else {
            emailSet.add(admin.email);
          }
        }

        // Check for duplicate phone numbers
        if (admin.phone?.trim()) {
          if (phoneSet.has(admin.phone.trim())) {
            errors[`adminDetails[${index}].phone`] =
              strings.displayText.duplicatePhoneError;
          } else {
            phoneSet.add(admin.phone.trim());
          }
        }
      });
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrors({});
    if (event.target.checked) {
      setFormData({
        ...formData,
        sameAsAbove: true,

        tradingStreetOne: formData.regStreetOne,
        tradingStreetTwo: formData.regStreetTwo,
        tradingStreetThree: formData.regStreetThree,
        tradingCity: formData.regCity,
        tradingState: formData.regState,
        tradingCountry: formData.regCountry,
        tradingPostalCode: formData.regPostalCode,
        tradingPhoneNumber: formData.regPhoneNumber,
        tradingEmail: formData.regEmail,
      });
    } else {
      setFormData({
        ...formData,
        sameAsAbove: false,
        tradingAddress: '',
        tradingStreetOne: '',
        tradingStreetTwo: '',
        tradingStreetThree: '',
        tradingCity: '',
        tradingState: '',
        tradingCountry: '',
        tradingPostalCode: '',
        tradingPhoneNumber: '',
        tradingEmail: '',
      });
    }
  };

  const handleSaveEmployer = () => {
    const missingFields = Object.keys(errors);
    if (handleFormValidate(formData)) {
      // save employer to database or API
      setLoader(true);
      createOrganization({
        ...(formData as any),
        adminDetails: formData?.adminDetails?.map((admin) => ({
          ...admin,
          phone:
            typeof admin.phone === 'string'
              ? admin.phone.replace(/^\+\+/, '+')
              : admin.phone,
          economicStatus:
            economicStatusAndOccupation?.categories?.find(
              (category) => category.id === admin.economicStatus,
            )?.subCategories?.length > 0
              ? admin.economicOccupation
              : admin.economicStatus,
        })),
      })
        .then((res: any) => {
          setFormData({
            companyName: '',
            payrollFrequency: '',
            regCity: '',
            regState: '',
            regCountry: '',
            regPostalCode: '',
            regPhoneNumber: '',
            regEmail: '',
            tradingCity: '',
            tradingState: '',
            tradingCountry: '',
            tradingPostalCode: '',
            tradingPhoneNumber: '',
            tradingEmail: '',
            employerTaxDetails: '',
            employerBankAccountName: '',
            payrollDate: '',
            createdByAcorn: false,
            comments: '',
            isActive: true,
            adminOption: 'later',
            adminDetails: [],
            sameAsAbove: false,
          });
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.employerCreatedSuccessText,
            }),
          );
          getupdateEmployerData();
          setLoader(false);
          onClose(false);
        })
        .catch((err) => {
          setLoader(false);
          dispatch(
            setSnackBarFailed({ snackBarMessage: err.response.data.message }),
          );
        });
    } else {
      if (formError.employerBankAccountNumber) {
        dispatch(
          setSnackBarFailed({
            snackBarMessage:
              strings.displayText.employerBankAccountNumberFormatCheck,
          }),
        );
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.fillMandatoryFields,
          }),
        );
      }
    }
  };

  return (
    <Fragment>
      <Box sx={{ maxWidth: '50rem' }}>
        <Box component="form" noValidate autoComplete="off">
          <Grid container>
            {employerInputField?.map((element, index) => (
              <Grid
                xs={
                  element.name === addEmployerConstantTexts.sameAsAbove
                    ? 12
                    : element.name === addEmployerConstantTexts.comments
                    ? 12
                    : 6
                }
                key={index}
                style={{
                  paddingLeft: 10,
                  paddingRight: 10,
                  marginBottom: 10,
                  marginTop: 5,
                }}
              >
                {element.name === addEmployerConstantTexts.regPhoneNumber ||
                element.name === addEmployerConstantTexts.tradingPhoneNumber ? (
                  <MuiPhoneNumber
                    required={Boolean(element.isRequired)}
                    enableLongNumbers={true}
                    label={element.label}
                    name={element.name}
                    defaultCountry={'ie'}
                    variant="outlined"
                    fullWidth
                    onChange={(value: string) =>
                      handlePhoneChange(value, element.name)
                    }
                    // onBlur={handleValidation}
                    value={element.value}
                    helperText={element.helperText}
                    sx={{
                      '& p': {
                        color: 'red',
                      },
                      my: 1,
                    }}
                  />
                ) : element.name ===
                  addEmployerConstantTexts.payrollFrequency ? (
                  <FormControl
                    fullWidth
                    style={{ marginTop: 8 }}
                    sx={{
                      '& p': {
                        color: 'red',
                      },
                    }}
                  >
                    <InputLabel>
                      {strings.displayText.payrollFrequencyLabel}
                    </InputLabel>
                    <Select
                      label={element.label}
                      name={addEmployerConstantTexts.payrollFrequency}
                      required={true}
                      value={formData.payrollFrequency}
                      onChange={handleInputChange}
                      style={{ width: '392px' }}
                    >
                      {payrollFrequencyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{element.helperText}</FormHelperText>
                  </FormControl>
                ) : element.name === addEmployerConstantTexts.comments ? (
                  <TextField
                    margin="dense"
                    fullWidth
                    label={element.label}
                    name={element.name}
                    onChange={handleInputChange}
                    value={element.value}
                    multiline
                    minRows={3}
                  />
                ) : element.name === addEmployerConstantTexts.sameAsAbove ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="success"
                        onChange={handleCheckbox}
                        checked={element.value}
                      />
                    }
                    label={element.label}
                    classes={{ label: classes.labelStyle }}
                  />
                ) : element.name === addEmployerConstantTexts.payrollDate ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    <TextField
                      margin="dense"
                      required={Boolean(element.isRequired)}
                      fullWidth
                      label={element.label}
                      name={element.name}
                      onChange={handleInputChange}
                      value={element.value}
                      sx={{
                        width: '100%',
                        '& p': {
                          color: 'red',
                        },
                      }}
                      type={element.type}
                      helperText={element.helperText}
                    />
                    <FormControlLabel
                      sx={{ mt: 0.5, ml: 0, mr: 0 }}
                      control={
                        <Checkbox
                          color="success"
                          name="createdByAcorn"
                          checked={Boolean(formData.createdByAcorn)}
                          onChange={handleSwitchInputChange}
                        />
                      }
                      label="Created By Acorn"
                      classes={{ label: classes.labelStyle }}
                    />
                  </Box>
                ) : (
                  <TextField
                    // inputRef={inputRef}
                    margin="dense"
                    required={Boolean(element.isRequired)}
                    fullWidth
                    label={element.label}
                    name={element.name}
                    onChange={handleInputChange}
                    // onBlur={handleValidation}
                    value={element.value}
                    sx={{
                      '& p': {
                        color: 'red',
                      },
                      // mb: 2,
                    }}
                    type={element.type}
                    // error={element.error}
                    helperText={element.helperText}
                    inputProps={
                      element.name === addEmployerConstantTexts.regPostalCode ||
                      element.name ===
                        addEmployerConstantTexts.tradingPostalCode
                        ? { maxLength: 8 }
                        : element.name ===
                          addEmployerConstantTexts.employerBankAccountNumber
                        ? { maxLength: BankAccountNumberLength }
                        : {}
                    }
                  />
                )}
              </Grid>
            ))}

            {/* <Grid item xs={6}>
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchInputChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Grid> */}

            <Grid
              item
              xs={6}
              // my={1}
              style={{ paddingLeft: 10, paddingRight: 10, marginTop: 12 }}
            >
              <FormControl fullWidth>
                <InputLabel id="admin-detail-select-label">
                  {strings.displayText.adminDetailsText}
                </InputLabel>
                <Select
                  labelId="admin-detail-select-label"
                  margin="normal"
                  value={formData.adminOption}
                  onChange={handleAdminDetailsChange}
                  label={strings.displayText.adminDetailsText}
                >
                  <MenuItem value="Add now">
                    {strings.displayText.addNowText}
                  </MenuItem>
                  <MenuItem value="Later">
                    {strings.displayText.laterText}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.adminOption === strings.displayText.addNowText && (
              <>
                {formData.adminDetails?.map(
                  (admin: IAdminDetails, index: number) => (
                    <Grid item xs={12}>
                      <Box key={index} mt={2}>
                        <Typography variant="subtitle1">
                          Admin {index + 1}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAdmin(index)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.keys(admin)?.map((field, i) => (
                            <Grid item xs={6} key={i}>
                              {field === 'phone' ? (
                                <MuiPhoneNumber
                                  sx={{
                                    '& p': {
                                      color: 'red',
                                    },
                                    mt: 1,
                                  }}
                                  label={strings.displayText.phoneNumber}
                                  name="phone"
                                  defaultCountry={'ie'}
                                  variant="outlined"
                                  fullWidth
                                  onChange={(value: string | undefined) =>
                                    handleAdminInputChange(
                                      index,
                                      undefined,
                                      value,
                                      'phone',
                                    )
                                  }
                                  value={
                                    typeof admin.phone === 'string' &&
                                    admin.phone.startsWith('++')
                                      ? admin.phone.substring(1)
                                      : admin.phone
                                  }
                                  helperText={
                                    formError?.adminDetails[index]?.phone
                                  }
                                />
                              ) : field === 'gender' ||
                                field === 'maritalStatus' ||
                                field === 'pepType' ||
                                field === 'economicStatus' ||
                                (field === 'economicOccupation' &&
                                  admin.economicStatus &&
                                  economicStatusAndOccupation?.categories?.find(
                                    (category) =>
                                      category.id === admin.economicStatus,
                                  )?.subCategories?.length > 0) ? (
                                <FormControl
                                  fullWidth
                                  sx={{
                                    '& p': {
                                      color: 'red',
                                    },
                                    mt: 1,
                                  }}
                                >
                                  <InputLabel id={field}>
                                    {fieldNames[field as keyof IAdminDetails]}
                                    {field !== 'pepType' && '*'}
                                  </InputLabel>
                                  <Select
                                    sx={{
                                      '& p': {
                                        color: 'red',
                                      },
                                    }}
                                    name={field}
                                    label={
                                      fieldNames[field as keyof IAdminDetails]
                                    }
                                    id={field}
                                    value={admin[field]}
                                    onChange={(event: any) =>
                                      handleAdminInputChange(
                                        index,
                                        event,
                                        event.target.value,
                                        field,
                                      )
                                    }
                                  >
                                    {getOptionItems(field, index)}
                                  </Select>
                                  <FormHelperText>
                                    {formError?.adminDetails[index]?.[
                                      field as keyof IAdminDetails
                                    ] || errors[field as keyof IAdminDetails]}
                                  </FormHelperText>
                                </FormControl>
                              ) : (
                                field !== 'economicOccupation' && (
                                  <TextField
                                    margin="dense"
                                    required={notRequiredFields(field)}
                                    fullWidth
                                    name={field}
                                    type={textFieldInputType(field)}
                                    label={
                                      fieldNames[field as keyof IAdminDetails]
                                    }
                                    onChange={(event: any) =>
                                      handleAdminInputChange(index, event)
                                    }
                                    // onBlur={(event: any) =>
                                    //   handleAdminInputValidation(index, event)
                                    // }
                                    value={admin[field as keyof IAdminDetails]}
                                    sx={{
                                      '& p': {
                                        color: 'red',
                                      },
                                    }}
                                    // error={errors[field as keyof IAdminDetails]}
                                    helperText={
                                      formError?.adminDetails[index]?.[
                                        field as keyof IAdminDetails
                                      ] || errors[field as keyof IAdminDetails]
                                    }
                                  />
                                )
                              )}
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  ),
                )}
                <Grid item xs={12}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                    py={1}
                  >
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddAdmin}
                    >
                      {strings.displayText.addAnotherAdminText}
                    </Button>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
          <Buttons
            // disabled={
            //   formError?.email !== undefined ||
            //   formError?.firstName !== undefined ||
            //   formError?.lastName !== undefined
            // }
            style={{ py: '0.8rem', mt: '1rem' }}
            onClick={handleSaveEmployer}
            fullWidth={true}
            endIcon={loader ? <CircularProgress size={15} /> : null}
            disabled={loader}
          >
            {strings.displayText.saveText}
          </Buttons>
        </Box>
      </Box>
    </Fragment>
  );
};

export default AddEmployer;
